import { useState } from "react";
import { Box, Typography, Chip, Switch } from "@mui/joy";
import { Shield } from "lucide-react";

import { ControlledTextField } from "../../../../../shared";
import type { IPrimaryAdminInvite } from "../../../../../interfaces";
import { ROLES } from "../../../../../constants/permissions";

export const DEFAULT_PRIMARY_ADMIN: IPrimaryAdminInvite = {
    FirstName: "",
    LastName: "",
    Email: "",
    RoleId: ROLES.ADMIN_OWNER,
    SkipInvite: false,
};

interface IPrimaryAdminProps {
    value?: IPrimaryAdminInvite;
    onChange?: (value: IPrimaryAdminInvite) => void;
    disabled?: boolean;
}

export function PrimaryAdmin({ value, onChange, disabled }: IPrimaryAdminProps) {
    const [internal, setInternal] = useState<IPrimaryAdminInvite>(DEFAULT_PRIMARY_ADMIN);
    const invite = value ?? internal;

    const update = (patch: Partial<IPrimaryAdminInvite>) => {
        const next = { ...invite, ...patch };
        if (!value) setInternal(next);
        onChange?.(next);
    };

    const fieldsDisabled = disabled || invite.SkipInvite;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                <Typography level="title-md">Primary admin</Typography>
            </Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                Creates a Cognito user and assigns them as Admin / Owner of this organization.
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    p: 1.5,
                    mb: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "4px",
                    bgcolor: "background.surface",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        Skip invite for now
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                        Create the organization without a primary admin. You can invite them later.
                    </Typography>
                </Box>
                <Switch
                    checked={invite.SkipInvite}
                    disabled={disabled}
                    onChange={(event) => update({ SkipInvite: event.target.checked })}
                    sx={{
                        flexShrink: 0,
                        "--Switch-thumbSize": "16px",
                        "--Switch-trackWidth": "36px",
                        "--Switch-trackHeight": "20px",
                    }}
                />
            </Box>

            <Box
                sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "4px",
                    bgcolor: invite.SkipInvite ? "background.level1" : "background.surface",
                    opacity: invite.SkipInvite ? 0.55 : 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    pointerEvents: invite.SkipInvite ? "none" : "auto",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "4px",
                        bgcolor: "background.level1",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            color: "primary.solidBg",
                            backgroundColor: "rgba(var(--standard-palette-primary-mainChannel) / 0.12)",
                        }}
                    >
                        <Shield size={16} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography level="title-sm">Admin / Owner</Typography>
                        <Typography level="body-xs" color="neutral">
                            Full access to projects, users, billing, and organization settings.
                        </Typography>
                    </Box>
                    <Chip size="sm" variant="soft" color="primary">
                        Fixed
                    </Chip>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                    }}
                >
                    <ControlledTextField
                        label="First name"
                        value={invite.FirstName}
                        disabled={fieldsDisabled}
                        placeholder="Jane"
                        onChange={(next: string) => update({ FirstName: next })}
                    />
                    <ControlledTextField
                        label="Last name"
                        value={invite.LastName}
                        disabled={fieldsDisabled}
                        placeholder="Doe"
                        onChange={(next: string) => update({ LastName: next })}
                    />
                </Box>

                <ControlledTextField
                    label="Work email"
                    helperText="Cognito will email a temporary password so they can sign in."
                    value={invite.Email}
                    disabled={fieldsDisabled}
                    type="email"
                    placeholder="jane@example.com"
                    onChange={(next: string) => update({ Email: next })}
                />
            </Box>
        </Box>
    );
}
